import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import SyncToCurrentVersionButton from "../components/SyncToCurrentVersionButton";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  MagnifyingGlass,
  Brain,
  Question,
  ListChecks,
  Microphone,
  ShieldWarning,
  ArrowsClockwise,
  Lightbulb,
  Note,
  SpinnerGap,
  X,
  Trash,
  BookOpen,
  TrendUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { mapLegacyToCanonical } from "../lib/canonicalRoles";
import { canEditContent, canAccessAdmin } from "../utils/permissions";

import { API_BASE as API } from "../lib/apiBase";
// Vincent #249 (Eric 2026-02-06) — Réutilise EXACTEMENT le composant ScriptsPage
// du menu de gauche en mode embedded pour l'onglet "Scripts de vente" de la BC,
// garantissant un rendu identique (V1/V2/V3 + validation + regen titre + édition).
import ScriptsPage from "./ScriptsPage";
// Refactor 2026-06-12 — composants et helpers extraits (zéro changement de logique).
import KnowledgeCard from "../components/knowledge/KnowledgeCard";
import { KnowledgeDetailModal } from "../components/knowledge/KnowledgeDetailModal";
import { TYPE_STYLES, formatRelativeTime, formatTitleHistoryEntry } from "../lib/knowledgeFormat";
// Vincent B702 (Eric 2026-02-11) — Palette unifiée avec les onglets vidéo :
// les libellés Q&R / Scripts / Procédures / Concepts / Objections / Synthèses
// reprennent la couleur exacte des traits verticaux de la timeline
// (TYPE_COLORS). Cohérence visuelle Explorateur ↔ KB.
import { TYPE_COLORS } from "../lib/videoDetailFormat";
// Vincent B590 (Eric 2026-07-29 SPEC STRICK) — Deep-link focus depuis
// Tableau de bord / Mes favoris. Ouvre le bon onglet ET scrolle sur l'item.
import { useFocusFromURL } from "../hooks/useFocusFromURL";
const EMPTY_STATE = "https://static.prod-images.emergentagent.com/jobs/9ce68c36-1b03-4868-a37f-38db73ced2af/images/ac1e39a43e42bf5a6e74d582c502572ddd5e50b14cd865b9eb668efafb6c961d.png";

const KNOWLEDGE_TYPES_KEYS = [
  // Vincent 2026-06-03 (Eric) — Ordre EXACT du menu de gauche :
  // Q&R · Scripts · Procédures · Concepts · Objections · Dictionnaire.
  // "Tous les types" reste en tête pour la vue agrégée.
  // Vincent B714 (Eric 2026-02-11) — Ajout onglet "Synthèses" pour lister
  // toutes les synthèses de vidéos / documents, avec les mêmes filtres et
  // tris que les autres types. Placé en dernier (après Objections) pour
  // ne pas modifier l'ordre historique attendu par Vincent.
  // Vincent B702 (Eric 2026-02-11) — `color` reprend TYPE_COLORS.<type>.color
  // pour aligner la palette KB sur la timeline vidéo (PanelTabsBar). "all"
  // reste neutre (agrégé multi-types).
  // Vincent B828 (Eric 2026-02-13) — « Synthèses » placé en 1er (avant
  // « Tous les types » et « FAQ ») pour aligner sur l'ordre du menu latéral
  // (B826) et refléter le fait que les Nouveaux employés (B827) débutent
  // ici par défaut. L'ordre historique fondateur ("all" en 1er) est cassé
  // volontairement suite à la demande explicite de Vincent.
  // Vincent B828bis (Eric 2026-02-14) — Ordre FINAL des onglets Base de
  // connaissances (gauche → droite) : Top 10 · Tous les types · Synthèses ·
  // Dictionnaire · Concepts · FAQ · Procédures · Scripts de vente · Objections.
  // Ici on n'expose que les onglets de type KU ; « Top 10 » (popular) et
  // « Dictionnaire » (lien) sont insérés à leur position exacte dans le
  // JSX (voir TAB_ORDER plus bas).
  // Vincent B875-final (Eric 2026-02-14) — Onglet "Tous les types" : hérite
  // par défaut de text-zinc-500 (rgb 113,113,122), contraste 4.4:1 sur
  // bg-zinc-100 (juste sous AA). On force zinc-600 (#52525b) via `color`
  // pour atteindre ≈ 7:1 (AA large).
  { value: "all", labelKey: "all_types", icon: Brain, color: "#52525b" },
  { value: "synthesis", labelKey: "syntheses_type", icon: Note, color: TYPE_COLORS.synthesis.color },
  { value: "concept", labelKey: "concepts_type", icon: Lightbulb, color: TYPE_COLORS.concept.color },
  { value: "qa", labelKey: "qa_type", icon: Question, color: TYPE_COLORS.qa.color },
  { value: "procedure", labelKey: "procedures_type", icon: ListChecks, color: TYPE_COLORS.procedure.color },
  { value: "script", labelKey: "scripts_type", icon: Microphone, color: TYPE_COLORS.script.color },
  { value: "objection", labelKey: "objections_type", icon: ShieldWarning, color: TYPE_COLORS.objection.color },
];

// Vincent B828bis (Eric 2026-02-14) — TAB_ORDER = ordre EXACT de rendu
// (gauche → droite) de la barre d'onglets Base de connaissances, incluant
// les onglets spéciaux "popular" (Top 10, bouton amber) et "dictionary"
// (lien vers la page Dictionnaire). Le kind détermine le composant rendu :
//   - "popular"    → TabsTrigger amber (activePanel = "popular")
//   - "type"       → TabsTrigger standard (activePanel = value KU)
//   - "dictionary" → <button> qui navigue vers /dashboard/dictionary
const TAB_ORDER = [
  { kind: "popular" },
  { kind: "type", value: "all" },
  { kind: "type", value: "synthesis" },
  { kind: "dictionary" },
  { kind: "type", value: "concept" },
  { kind: "type", value: "qa" },
  { kind: "type", value: "procedure" },
  { kind: "type", value: "script" },
  { kind: "type", value: "objection" },
];

export default function KnowledgePage({ initialTab = "all" } = {}) {
  // Vincent 2026-05-24 — Lire le tab depuis l'URL `?tab=qa|script|procedure|concept|objection`
  // pour permettre aux items de menu (FAQ, Concepts, Objections, Scripts, Procédures) d'aller directement
  // sur la bonne vue filtrée. Fallback sur initialTab prop puis "all".
  const location = useLocation();
  // Vincent B827 (Eric 2026-02-13) — On lit `user` AVANT `useState(activeTab)`
  // pour choisir le tab par défaut selon le rôle canonique (Nouvel employé →
  // « Synthèses » au lieu de « Tous les types »). Exclut fondateurs/admins/
  // concepteurs même s'ils ont access_level=debutant côté progression.
  const { user } = useAuth();
  const _validTabs = ["all", "qa", "script", "procedure", "concept", "objection", "rebuttal", "synthesis"];
  const _isNewEmployee =
    !!user &&
    !user.is_founder &&
    !user.is_super_admin &&
    user.role !== "admin" &&
    user.role !== "concepteur" &&
    mapLegacyToCanonical(user.role || user.access_level) === "employee_new";
  const _readTabFromSearch = () => {
    const p = new URLSearchParams(location.search || "");
    const t = p.get("tab");
    if (_validTabs.includes(t)) return t;
    // Vincent B827 — Pas de tab dans l'URL + rôle canonique Nouvel employé
    // + initialTab (prop) === "all" → défaut = "synthesis".
    if (_isNewEmployee && initialTab === "all") return "synthesis";
    return initialTab;
  };
  const [activeTab, setActiveTab] = useState(_readTabFromSearch());
  // Vincent 2026-06-01 — Bug interface : quand on bascule entre FAQ/Concepts/Procédure
  // dans le menu de gauche, l'onglet correspondant dans la page Base de connaissances
  // n'était pas resélectionné car useState ne se re-init pas au changement de query.
  // Re-sync à chaque changement de location.search.
  useEffect(() => {
    const t = _readTabFromSearch();
    setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const KNOWLEDGE_TYPES = KNOWLEDGE_TYPES_KEYS.map(kt => ({ ...kt, label: t(kt.labelKey) }));
  const [searchQuery, setSearchQuery] = useState("");
  const [filterVideo, setFilterVideo] = useState("");
  const [filterSpeaker, setFilterSpeaker] = useState("");
  // Vincent XLSX 2026-07-09 15h20 Row #6 (Eric 2026-07-09 SPEC STRICK) —
  // Refonte complète des critères de tri de la base de connaissances. La
  // liste EXACTE demandée par Vincent (8 options, dans cet ordre) :
  //   catégorie (A à Z / Z à A), date de création (croiss/décroiss),
  //   horodatage (croiss/décroiss), titre (A à Z / Z à A).
  // Défaut = titre (A à Z).
  const [sortBy, setSortBy] = useState("title_asc");
  // Vincent #125 (Eric 2026-05-13) — La base de connaissances doit montrer
  // TOUTES les connaissances (validées + en révision). Avant, le code forçait
  // status=approved côté backend → 80% des connaissances étaient cachées
  // (102/125 dans knos-platform). Filtre UI optionnel par statut.
  const [statusFilter, setStatusFilter] = useState("all"); // all | approved | pending
  const [units, setUnits] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  // Vincent B770 (Eric 2026-02-17 SPEC STRICK PROD) — Onglet "Top 10 populaires"
  // dans la Base de connaissances : agrège les citations RAG des 90 derniers
  // jours pour révéler les KUs les plus utiles aux apprenants de l'org.
  const [popularItems, setPopularItems] = useState(null);
  const [popularLoading, setPopularLoading] = useState(false);
  useEffect(() => {
    if (activeTab !== "popular") return;
    if (popularItems !== null || popularLoading) return;
    setPopularLoading(true);
    axios.get(`${API}/api/knowledge/popular?days=90&limit=10`, { withCredentials: true })
      .then((r) => setPopularItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => {
        toast.error(lang === "fr" ? "Échec du chargement des populaires" : "Popular load failed");
        setPopularItems([]);
      })
      .finally(() => setPopularLoading(false));
  }, [activeTab]);

  // Vincent B590 (Eric 2026-07-29 SPEC STRICK) — Deep-link focus.
  // Le paramètre `?focus=<unit_id>` scrolle et surligne la KnowledgeCard
  // correspondante. Re-tenté après changement de onglet/rendu de units.
  useFocusFromURL([activeTab, units.length]);

  useEffect(() => {
    loadData();
    loadFavorites();
    // Vincent #122 (Eric 2026-05-15) — Recharger quand l'utilisateur bascule
    // de role via le RoleSwitcher (Vue : Stagiaire / Conseiller / etc.).
    // Sans cela, un Fondateur en mode "Vue : Stagiaire" continuait a voir
    // les Q&R pending_review jusqu'au prochain refresh manuel.
    // Backup defense: listen to the global "knoria:user-changed" event
    // dispatched by RoleSwitcher in case useEffect deps miss something.
    const onUserChange = () => { loadData(); loadFavorites(); };
    window.addEventListener("knoria:user-changed", onUserChange);
    return () => window.removeEventListener("knoria:user-changed", onUserChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, statusFilter, user?.view_as_role, user?.is_simulating]);

  const loadFavorites = async () => {
    try {
      const { data } = await axios.get(`${API}/api/favorites`, { withCredentials: true });
      setFavoriteIds(new Set(data.filter(f => f.item_type === "knowledge").map(f => f.item_id)));
    } catch {}
  };

  const toggleFavorite = async (e, unitId) => {
    e.stopPropagation();
    // Vincent B546/B556 (Eric 2026-07-27) — 3 correctifs :
    //   1. `_allowRetry: true` : autorise le retry du POST/DELETE en cas de
    //      transient 502/504/520 (bouclier Cloudflare). Backend maintenant
    //      idempotent, donc retry sécuritaire.
    //   2. Toast d'erreur explicite (au lieu de `catch {}` silencieux) pour
    //      que l'utilisateur sache quand ça foire vraiment.
    //   3. Optimistic update : on met à jour l'état AVANT la requête. Si la
    //      requête échoue, on rollback + toast.
    const wasFavorite = favoriteIds.has(unitId);
    // Optimistic update
    setFavoriteIds(prev => {
      const s = new Set(prev);
      if (wasFavorite) s.delete(unitId); else s.add(unitId);
      return s;
    });
    try {
      if (wasFavorite) {
        await axios.delete(`${API}/api/favorites/knowledge/${unitId}`, { withCredentials: true, _allowRetry: true });
      } else {
        await axios.post(`${API}/api/favorites`, { type: "knowledge", item_id: unitId }, { withCredentials: true, _allowRetry: true });
      }
    } catch (err) {
      // Rollback optimistic
      setFavoriteIds(prev => {
        const s = new Set(prev);
        if (wasFavorite) s.add(unitId); else s.delete(unitId);
        return s;
      });
      const msg = err?.response?.data?.detail || err?._friendlyMessage || "Impossible de mettre à jour le favori. Réessayez.";
      toast.error(msg);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab !== "all") {
        params.type = activeTab;
      }
      // Vincent #125 (Eric 2026-05-13) — Affiche TOUTES les connaissances
      // par défaut. Le filtre statut est appliqué côté client (UI-only) pour
      // garder une seule requête par changement d'onglet.
      if (statusFilter === "approved") params.status = "approved";
      else if (statusFilter === "pending") params.status = "pending_review";
      else if (statusFilter === "rejected") params.status = "rejected";

      const [unitsRes, videosRes] = await Promise.all([
        axios.get(`${API}/api/knowledge`, { params, withCredentials: true }),
        axios.get(`${API}/api/videos`, { withCredentials: true })
      ]);

      // Vincent 2026-06-02 (Eric) — Inclure les procédures à nouveau pour que
      // les onglets miroir le menu de gauche. La page /dashboard/procedures
      // reste accessible séparément.
      const rawUnits = Array.isArray(unitsRes.data) ? unitsRes.data : (unitsRes.data?.items || []);
      setUnits(rawUnits);
      setVideos(videosRes.data.items || videosRes.data);
    } catch (err) {
      console.error("Failed to load knowledge:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUnits = units.filter(unit => {
    // Video filter
    if (filterVideo && unit.video_id !== filterVideo) return false;
    // Speaker filter (check source_text or content for speaker name)
    if (filterSpeaker) {
      const content = JSON.stringify(unit.content || {}).toLowerCase();
      const sourceText = (unit.source_text || "").toLowerCase();
      if (!content.includes(filterSpeaker.toLowerCase()) && !sourceText.includes(filterSpeaker.toLowerCase())) return false;
    }
    // Search query
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      unit.title?.toLowerCase().includes(query) ||
      JSON.stringify(unit.content)?.toLowerCase().includes(query)
    );
  });

  // Vincent XLSX 2026-07-09 15h20 Row #6 — 8 options exactes.
  // Rétrocompat : anciennes valeurs (chrono, alpha, alpha_desc, category, video)
  // mappées ci-dessous sur les nouvelles.
  const videoById = videos.reduce((acc, v) => { acc[v.id] = v; return acc; }, {});
  const _getTs = (u) => Number(u.timestamp ?? u.timestamps?.start ?? Number.POSITIVE_INFINITY);
  const _getCategory = (u) => (
    (videoById[u.video_id]?.category || videoById[u.video_id]?.training_theme || "")
      .toLowerCase()
  );
  const _cmpTitle = (a, b) => (a.title || "").localeCompare(b.title || "", lang === "fr" ? "fr" : "en", { sensitivity: "base" });
  const sortedUnits = [...filteredUnits].sort((a, b) => {
    switch (sortBy) {
      case "category_asc": {
        const ca = _getCategory(a);
        const cb = _getCategory(b);
        if (ca !== cb) return ca.localeCompare(cb, "fr", { sensitivity: "base" });
        return _cmpTitle(a, b);
      }
      case "category_desc": {
        const ca = _getCategory(a);
        const cb = _getCategory(b);
        if (ca !== cb) return cb.localeCompare(ca, "fr", { sensitivity: "base" });
        return _cmpTitle(a, b);
      }
      case "created_asc": {
        const da = new Date(a.created_at || 0).getTime();
        const db = new Date(b.created_at || 0).getTime();
        return da - db;
      }
      case "created_desc":
      case "chrono": {  // rétrocompat : ancien "chrono" = récent d'abord
        const da = new Date(a.created_at || 0).getTime();
        const db = new Date(b.created_at || 0).getTime();
        return db - da;
      }
      case "timestamp_asc": {
        const ta = _getTs(a);
        const tb = _getTs(b);
        if (ta !== tb) return ta - tb;
        return _cmpTitle(a, b);
      }
      case "timestamp_desc": {
        const ta = _getTs(a);
        const tb = _getTs(b);
        if (ta !== tb) return tb - ta;
        return _cmpTitle(a, b);
      }
      case "title_desc":
      case "alpha_desc":  // rétrocompat
        return -_cmpTitle(a, b);
      case "title_asc":
      case "alpha":  // rétrocompat
      default:
        return _cmpTitle(a, b);
    }
  });

  const uniqueSpeakers = [...new Set(units.flatMap(u => {
    const matches = (u.source_text || "").match(/^(Speaker \d+|[A-Z][a-z]+ [A-Z][a-z]+)/gm);
    return matches || [];
  }))].filter(Boolean);

  const getVideoTitle = (videoId) => {
    const video = videos.find(v => v.id === videoId);
    return video?.title || t("untitled");
  };

  const formatTimestamp = (seconds) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const params = {};
      if (activeTab !== "all") params.type = activeTab;
      
      const response = await axios.get(`${API}/api/knowledge/export-pdf`, {
        params,
        withCredentials: true,
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `KNORIA_Guide_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t("error") + " : " + (err.response?.status === 404 ? t("no_knowledge") : t("error")));
    } finally {
      setExporting(false);
    }
  };

  // Vincent 2026-05-06 — Bulk regen des titres Q&R (questions tronquees / interrogatives)
  const [regenBatching, setRegenBatching] = useState(false);
  const [backfillingObj, setBackfillingObj] = useState(false);

  const handleBackfillObjections = async () => {
    if (backfillingObj) return;
    setBackfillingObj(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/backfill-objections`,
        {},
        { withCredentials: true, timeout: 300000 }
      );
      toast.success(
        lang === "fr"
          ? `${data.objections_created} objection(s) extraite(s) depuis ${data.scripts_with_obj}/${data.scripts_scanned} scripts.`
          : `${data.objections_created} objection(s) extracted from ${data.scripts_with_obj}/${data.scripts_scanned} scripts.`
      );
      // Reload so the new objections appear
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec du backfill objections" : "Backfill objections failed"));
    } finally {
      setBackfillingObj(false);
    }
  };
  const handleRegenAllTitles = async () => {
    if (regenBatching) return;
    const ok = await confirmDialog("Régénérer automatiquement tous les titres défectueux (interrogatifs ou trop longs) ? Cette action utilise GPT-5.2 et peut prendre quelques minutes.");
    if (!ok) return;
    setRegenBatching(true);
    try {
      const { data } = await axios.post(
        `${API}/api/agent/knowledge-units/regenerate-bad-titles`,
        {},
        { withCredentials: true, timeout: 300000 }
      );
      toast.success(`${data.updated} titre(s) régénéré(s) sur ${data.total_candidates} défectueux. ${data.failed} échec(s).`);
      // reload list
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la régénération en lot");
    } finally {
      setRegenBatching(false);
    }
  };

  const TypeIcon = ({ type }) => {
    const config = KNOWLEDGE_TYPES.find(t => t.value === type);
    const Icon = config?.icon || Brain;
    return <Icon size={16} />;
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">{t("knowledge_base")}</h1>
          <p className="text-zinc-500 mt-1">{t("upload_to_build")}</p>
        </div>
        <div className="flex gap-2">
          {/* Eric 2026-06-25 (iter118 B3) — migré vers canAccessAdmin. */}
          {canAccessAdmin(user) ? (
            <SyncToCurrentVersionButton onSynced={() => loadData()} />
          ) : null}
          {canAccessAdmin(user) && activeTab === "objection" ? (
            <Button
              onClick={handleBackfillObjections}
              disabled={backfillingObj}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="btn-backfill-objections"
              title={lang === "fr"
                ? "Extraire les objections de tous les scripts existants vers cette base"
                : "Extract objections from all existing scripts into this base"}
            >
              {backfillingObj ? (
                <SpinnerGap size={16} className="animate-spin" />
              ) : (
                <ArrowsClockwise size={18} className="text-teal-600" />
              )}
              {backfillingObj
                ? (lang === "fr" ? "En cours…" : "Running…")
                : (lang === "fr" ? "Extraire des scripts" : "Extract from scripts")}
            </Button>
          ) : null}
          {/* Vincent #24 (Eric 2026-06-04, fichier 17h) — Demande EXPLICITE de
              supprimer "Régénérer titres", "Régénérer scripts", "Importer Q&R",
              "Données à jour" et "Exporter PDF" du header. Les actions
              équivalentes restent dans les onglets dédiés et l'agent. */}
          {/* Vincent #17 batch FLHQ (Eric 2026-02-06) → Eric 2026-06-25 (iter118
              B3) — Bouton "Corbeille" : accessible aux éditeurs (canEditContent),
              cohérent avec la page elle-même (KnowledgeTrashPage). */}
          {canEditContent(user) && (
            <Button
              onClick={() => navigate("/dashboard/knowledge/trash")}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="btn-knowledge-trash"
              title={lang === "fr" ? "Voir la corbeille (connaissances effacées)" : "View trash (deleted knowledge)"}
            >
              🗑 {lang === "fr" ? "Corbeille" : "Trash"}
            </Button>
          )}
        </div>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("search_knowledge")}
            className="pl-10 h-11 rounded-sm"
            data-testid="knowledge-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <select value={filterVideo} onChange={e => setFilterVideo(e.target.value)} className="h-11 px-3 border border-zinc-200 rounded-lg text-sm bg-white" data-testid="filter-video">
          <option value="">{lang === "fr" ? "Toutes les vidéos" : "All videos"}</option>
          {videos.map(v => <option key={v.id} value={v.id}>{v.title?.substring(0, 40)}</option>)}
        </select>
        {/* Vincent #125 (Eric 2026-05-13) — Filtre statut : Tous / Validées / En révision.
            Par défaut "Tous" pour respecter l'exigence : toutes les connaissances,
            qu'elles soient validées ou non, sont placées dans cette base. */}
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="h-11 px-3 border border-zinc-200 rounded-lg text-sm bg-white"
          data-testid="knowledge-status-filter"
          title={lang === "fr" ? "Filtrer par statut de validation" : "Filter by validation status"}
        >
          <option value="all">{lang === "fr" ? "Tous statuts" : "All statuses"}</option>
          <option value="approved">{lang === "fr" ? "Validées seulement" : "Approved only"}</option>
          <option value="pending">{lang === "fr" ? "En révision seulement" : "Pending only"}</option>
          {/* Vincent #252 (2026-02-06) — Filtre Refusées pour audit Loi 25. */}
          <option value="rejected">{lang === "fr" ? "Refusées seulement" : "Rejected only"}</option>
        </select>
        {/* Vincent XLSX 2026-07-09 15h20 Row #6 (Eric 2026-07-09 SPEC STRICK) —
            Nouvelle liste de tri : 8 options EXACTES demandées. Défaut = titre A→Z. */}
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="h-11 px-3 border border-zinc-200 rounded-lg text-sm bg-white"
          data-testid="knowledge-sort-select"
          title={lang === "fr" ? "Trier les résultats" : "Sort results"}
        >
          <option value="category_asc">{lang === "fr" ? "catégorie (A à Z)" : "category (A to Z)"}</option>
          <option value="category_desc">{lang === "fr" ? "catégorie (Z à A)" : "category (Z to A)"}</option>
          <option value="created_asc">{lang === "fr" ? "date de création (croissante)" : "creation date (ascending)"}</option>
          <option value="created_desc">{lang === "fr" ? "date de création (décroissante)" : "creation date (descending)"}</option>
          <option value="timestamp_asc">{lang === "fr" ? "horodatage (croissant)" : "timestamp (ascending)"}</option>
          <option value="timestamp_desc">{lang === "fr" ? "horodatage (décroissant)" : "timestamp (descending)"}</option>
          <option value="title_asc">{lang === "fr" ? "titre (A à Z)" : "title (A to Z)"}</option>
          <option value="title_desc">{lang === "fr" ? "titre (Z à A)" : "title (Z to A)"}</option>
        </select>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => {
        setActiveTab(v);
        // Vincent 2026-05-24 — sync URL pour partage de liens directs vers un type
        if (typeof window !== "undefined") {
          const url = new URL(window.location.href);
          if (v === "all") url.searchParams.delete("tab"); else url.searchParams.set("tab", v);
          window.history.replaceState({}, "", url.toString());
        }
      }} className="mb-6">
        <TabsList className="w-full sm:w-auto flex flex-wrap gap-1 h-auto p-1 bg-zinc-100 rounded-sm">
          {/* Vincent B828bis (Eric 2026-02-14) — Rendu piloté par TAB_ORDER
              pour garantir l'ordre EXACT (gauche → droite) demandé :
              Top 10 · Tous les types · Synthèses · Dictionnaire · Concepts ·
              FAQ · Procédures · Scripts de vente · Objections. */}
          {TAB_ORDER.map((entry, idx) => {
            if (entry.kind === "popular") {
              // Vincent B770 (Eric 2026-02-17 SPEC STRICK PROD) — Onglet
              // "Top 10 populaires" : classement des KUs les plus citées
              // par le chatbot dans les 90 derniers jours (peer discovery).
              return (
                <TabsTrigger
                  key="popular"
                  value="popular"
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-sm data-[state=active]:bg-amber-500 data-[state=active]:text-white data-[state=active]:shadow-sm ${
                    activeTab === "popular" ? "font-bold" : "font-medium text-amber-800"
                  }`}
                  data-testid="tab-popular"
                  title={lang === "fr"
                    ? "Les 10 connaissances les plus citées par le chatbot dans les 90 derniers jours"
                    : "The 10 knowledge units most cited by the chatbot in the past 90 days"}
                >
                  <TrendUp size={16} weight="bold" />
                  <span className="hidden sm:inline">{lang === "fr" ? "Top 10" : "Top 10"}</span>
                </TabsTrigger>
              );
            }
            if (entry.kind === "dictionary") {
              // Vincent 2026-06-02 (Eric) — Dictionnaire = page séparée (jargon
              // métier), ce n'est pas un type de KU. On présente un lien-onglet
              // qui navigue vers /dashboard/dictionary pour que la BC miroir
              // le menu de gauche.
              return (
                <button
                  key="dictionary"
                  type="button"
                  onClick={() => navigate("/dashboard/dictionary")}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-sm text-zinc-700 hover:bg-white hover:shadow-sm transition-colors"
                  data-testid="tab-dictionary-link"
                  title={lang === "fr" ? "Ouvrir le Dictionnaire (page dédiée)" : "Open the Dictionary (dedicated page)"}
                >
                  <BookOpen size={16} />
                  <span className="hidden sm:inline">{t("dictionary")}</span>
                </button>
              );
            }
            // kind === "type"
            const type = KNOWLEDGE_TYPES.find((k) => k.value === entry.value);
            if (!type) return null;
            return (
              <TabsTrigger
                key={type.value}
                value={type.value}
                // Vincent B702 (Eric 2026-02-11) — Couleur type sur le libellé
                // (héritée de la timeline vidéo). L'onglet actif se signale
                // par font-bold + fond blanc (déjà géré via data-[state=active]).
                style={type.color ? { color: type.color } : undefined}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm ${
                  activeTab === type.value ? "font-bold" : "font-medium"
                }`}
                data-testid={`tab-${type.value}`}
              >
                <type.icon size={16} />
                <span className="hidden sm:inline">{type.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <SpinnerGap size={32} className="animate-spin text-[#002FA7]" />
        </div>
      ) : activeTab === "popular" ? (
        /* Vincent B770 (Eric 2026-02-17 SPEC STRICK PROD) — Top 10 populaires */
        popularLoading ? (
          <div className="flex items-center justify-center gap-2 text-muted-foreground py-12" data-testid="knowledge-popular-loading">
            <SpinnerGap size={20} className="animate-spin" /> {lang === "fr" ? "Chargement…" : "Loading…"}
          </div>
        ) : (popularItems || []).length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm" data-testid="knowledge-popular-empty">
            {lang === "fr"
              ? "Aucune citation dans les 90 derniers jours. Utilise le chatbot pour révéler tes connaissances vedettes."
              : "No citations in the last 90 days. Use the chatbot to reveal your star knowledge units."}
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="knowledge-popular-list">
            <div className="text-[11px] text-muted-foreground italic px-1">
              {lang === "fr"
                ? "Basé sur les citations du chatbot des 90 derniers jours dans ton organisation."
                : "Based on chatbot citations in your organization over the past 90 days."}
            </div>
            {popularItems.map((ku, i) => (
              <div key={ku.id} className="flex items-center gap-2" data-testid={`knowledge-popular-row-${i}`}>
                <span className="text-[13px] font-bold text-amber-700 tabular-nums shrink-0 w-8 text-center" data-testid={`knowledge-popular-rank-${i}`}>
                  #{i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <KnowledgeCard
                    unit={ku}
                    videoTitle={getVideoTitle(ku.video_id)}
                    formatTimestamp={formatTimestamp}
                    onClick={() => setSelectedUnit(ku)}
                    isFavorite={favoriteIds.has(ku.id)}
                    onToggleFavorite={undefined}
                  />
                </div>
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5 shrink-0"
                  data-testid={`knowledge-popular-count-${i}`}
                  title={lang === "fr" ? `Citée ${ku.citation_count} fois par le chatbot` : `Cited ${ku.citation_count} times by chatbot`}
                >
                  <TrendUp size={11} weight="bold" />
                  {ku.citation_count}
                </span>
              </div>
            ))}
          </div>
        )
      ) : activeTab === "script" ? (
        /* Vincent #249 (Eric 2026-02-06) — Onglet Scripts de vente : rendu
           identique à /dashboard/scripts via embed du composant ScriptsPage,
           pour cohérence parfaite avec le menu de gauche. */
        <div data-testid="knowledge-scripts-embedded">
          <ScriptsPage embedded={true} />
        </div>
      ) : sortedUnits.length > 0 ? (
        /* Vincent #249 ext (Eric 2026-02-06) — Liste 1 colonne pleine largeur,
           cohérente avec ScriptsPage embedded sur l'onglet "Scripts" (et donc
           uniforme sur tous les onglets de la BC : Q&R, Scripts, Procédures,
           Concepts, Objections). */
        <div className="space-y-2.5">
          {sortedUnits.map((unit) => (
            <KnowledgeCard
              key={unit.id}
              unit={unit}
              videoTitle={getVideoTitle(unit.video_id)}
              formatTimestamp={formatTimestamp}
              onClick={() => setSelectedUnit(unit)}
              isFavorite={favoriteIds.has(unit.id)}
              onToggleFavorite={toggleFavorite}
              searchQuery={searchQuery}
              onTitleRegenerated={(id, newTitle) => {
                setUnits((prev) => prev.map((u) => u.id === id ? { ...u, title: newTitle } : u));
              }}
            />
          ))}
        </div>
      ) : (
        <div className="py-16 flex flex-col items-center justify-center text-center">
          <img src={EMPTY_STATE} alt="No results" className="w-48 h-48 mb-4 opacity-60" />
          <h3 className="font-medium text-zinc-900">{t("no_knowledge")}</h3>
          <p className="text-sm text-zinc-500 mt-1">
            {searchQuery 
              ? t("try_different_search")
              : t("upload_to_build")
            }
          </p>
        </div>
      )}

      {/* Détail modal */}
      {selectedUnit && (
        // Vincent B1 XLS 2026-08-10 (Eric 2026-02-10) — Navigation
        // Précédent/Suivant à l'intérieur du modal d'édition depuis la
        // Base de connaissances (déjà supportée par KnowledgeDetailModal
        // depuis B621.b). Index calculé inline via findIndex sur la liste
        // filtrée courante.
        <KnowledgeDetailModal
          unit={selectedUnit}
          videoTitle={getVideoTitle(selectedUnit.video_id)}
          formatTimestamp={formatTimestamp}
          isAdmin={canEditContent(user)}
          onClose={() => setSelectedUnit(null)}
          onNavigatePrev={
            units.findIndex((u) => u.id === selectedUnit.id) > 0
              ? () => {
                  const i = units.findIndex((u) => u.id === selectedUnit.id);
                  setSelectedUnit(units[i - 1]);
                }
              : undefined
          }
          onNavigateNext={
            (() => {
              const i = units.findIndex((u) => u.id === selectedUnit.id);
              return i >= 0 && i < units.length - 1;
            })()
              ? () => {
                  const i = units.findIndex((u) => u.id === selectedUnit.id);
                  setSelectedUnit(units[i + 1]);
                }
              : undefined
          }
          navigationLabel={(() => {
            const i = units.findIndex((u) => u.id === selectedUnit.id);
            return i >= 0 ? `${i + 1} / ${units.length}` : undefined;
          })()}
          onUpdated={(updated) => {
            setSelectedUnit(updated);
            setUnits((prev) => prev.map((u) => u.id === updated.id ? { ...u, ...updated } : u));
          }}
          onDeleted={(deletedId) => {
            setSelectedUnit(null);
            setUnits((prev) => prev.filter((u) => u.id !== deletedId));
          }}
        />
      )}
    </div>
  );
}
